// A DURABLE LINK TO A RECORD whose ordinary click opens an ephemeral quick view.
//
// It lived inside `preview-card.tsx` while a table cell was the only thing that opened a
// record. The org chart's person card is the second, and a second `<Link>` composing this
// address beside it would be two implementations of the rule below — the one that is
// invisible when it drifts, because both spellings navigate and only one of them keeps the
// page underneath.

import {
  type ComponentProps,
  createContext,
  type MouseEvent,
  type ReactNode,
  useContext,
} from "react";
import { Link, useLocation, useNavigate } from "react-router";
// THE FORMAT COMES FROM THE FILE THAT OWNS IT, and this import is the correction of a claim
// this file used to make. It said `ui/` may not import from `app/` — a convention rather
// than a rule, and one `ui/account.tsx` and `ui/person.tsx` already cross for `recordAt`,
// as `ui/data-table.tsx` does for `addressOf`. Restating `openingOver` here to honour a
// boundary nothing enforces would be two implementations of one URL shape, which is a
// worse trade than the import: they would agree today and part company silently, and the
// half that drifted would be the one composing every address a reader sees.
import { openingOver } from "../app/records";

/**
 * WHICH RECORDS ARE ALREADY OPEN, bottom of the stack first. Empty on a screen.
 *
 * A press inside a record pane opens the next record ON TOP of it, and the only thing that
 * can say what is already open is the thing rendering it. So the shell's pane publishes the
 * addresses and a link inside it composes the next history state. The state belongs to the
 * browser entry rather than to a component-local `useState`, which is why Back can unwind it.
 *
 * WHY NOT READ `location.state` DIRECTLY: what the shell publishes is the stack AFTER the
 * limit and the cycle cut, while the raw state is what was requested. Composing from the raw
 * request would append a record per press for ever and retain levels the screen no longer
 * shows.
 *
 * **IT SURVIVED A DELETION THAT TOOK EVERYTHING AROUND IT, AND THAT IS DELIBERATE.** Moving
 * earlier route work removed `pageUnder`, `state.from` and `state.over`; this context is the
 * only piece that still has a job. It carries the stack AS BUILT, which only the component
 * applying the limit and the cycle cut can know. Deleting it would let the next press revive
 * levels the screen has already removed.
 *
 * `record-pane.tsx` provides it, and nothing else may.
 */
const Beneath = createContext<readonly string[]>([]);
const ReviewSequence = createContext<readonly string[] | null>(null);

/** Only the source collection can declare its already loaded, visible review order. */
export function RecordReviewSequence({
  addresses,
  children,
}: {
  readonly addresses: readonly string[] | null;
  readonly children: ReactNode;
}) {
  return <ReviewSequence.Provider value={addresses}>{children}</ReviewSequence.Provider>;
}

interface LinkLocation {
  readonly pathname: string;
  readonly search: string;
  readonly state: unknown;
}

/**
 * THE REAL BROWSER ENTRY, published outside the page router.
 *
 * The page router is deliberately matched at a bare pathname so opening a pane does not
 * rebuild the whole screen. A `useLocation()` below that overridden `<Routes>` therefore
 * cannot see the page's own query or the entry's state. Only record links need those facts,
 * so `Located` publishes them here without making the page component subscribe to routing.
 */
const CurrentEntry = createContext<LinkLocation | null>(null);

export function RecordLinkLocation({
  location,
  children,
}: {
  readonly location: LinkLocation;
  readonly children: ReactNode;
}) {
  return <CurrentEntry.Provider value={location}>{children}</CurrentEntry.Provider>;
}

/** The record addresses open beneath a reference pressed in here. */
export function useRecordsBeneath(): readonly string[] {
  return useContext(Beneath);
}

/**
 * WHAT IS ALREADY OPEN, published around the panes that are open.
 *
 * `records` is the addresses in stack order, so `records[0]` is the pane at the bottom.
 * Nothing outside `record-pane.tsx` renders one.
 */
export function RecordsBeneath({
  records,
  children,
}: {
  readonly records: readonly string[];
  readonly children: ReactNode;
}) {
  return <Beneath.Provider value={records}>{children}</Beneath.Provider>;
}

/** The actual history entry, including query/state outside a pathname-only page match. */
export function useCurrentEntry(): LinkLocation {
  const routed = useLocation();
  return useContext(CurrentEntry) ?? routed;
}

/** Compose and perform the one quick-view navigation used by links and view-only buttons. */
export function useOpenQuickView(to: ComponentProps<typeof Link>["to"]): () => void {
  const location = useCurrentEntry();
  const navigate = useNavigate();
  const beneath = useRecordsBeneath();
  const sequence = useContext(ReviewSequence);
  const opening = openingOver(
    `${location.pathname}${location.search}`,
    [...beneath, `${to}`],
    location.state,
    sequence,
  );

  return () => {
    void navigate(opening.to, { state: opening.state });
  };
}

/**
 * A durable link to a record whose ordinary click opens it over the current page.
 *
 * `href` remains the record's own address — `/people/{id}`, what `recordAt` builds. That
 * matters: copy-link and a modified click/new tab must reach the permanent detail screen.
 * An unmodified primary click is intercepted and pushes the same visible page with the
 * quick-view stack in React Router history state. Back closes it, while the address bar
 * continues to say `/leave`.
 *
 * THE COMPOSITION IS STRING WORK ON AN ADDRESS IT WAS HANDED, which is what lets this stay
 * in `ui/`: it knows a record's address is a string, not what a record IS. `records.ts`
 * owns the vocabulary and `ui/` may not import from `app/`.
 *
 * IT IS A COMPONENT so the hooks are only called where there IS a destination. A reference
 * with nowhere to go renders a button and has no business knowing what page it is on —
 * and `Previewed` is used inside panes that tests render without a router, where reading
 * the location would be a crash rather than a missing feature.
 */
export function RecordLink({ to, onClick, ...rest }: ComponentProps<typeof Link>) {
  const open = useOpenQuickView(to);

  const openQuickView = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    open();
  };

  // EVERYTHING ELSE IS FORWARDED, and it is not housekeeping. Base UI's `render` prop
  // CLONES this element and injects the trigger's handlers, `aria-*` and ref onto it — so
  // a component that named only the props it cared about swallowed all of them, and the
  // card stopped opening while the link still worked. The failure looked like a missing
  // popup rather than a dropped prop. `ActionLink` clones it the same way, for the
  // className that makes it look like a button.
  return <Link {...rest} to={to} onClick={openQuickView} />;
}

/**
 * A record entrance that deliberately has no durable full-page address.
 *
 * Self-service summaries use this when the record may be inspected in context but its
 * administrative register is not a destination on that surface. It remains a real button,
 * so the row accelerator, keyboard focus and accessible name all point at the same control.
 */
export function RecordPeek({
  to,
  onClick,
  type = "button",
  ...rest
}: ComponentProps<"button"> & { readonly to: ComponentProps<typeof Link>["to"] }) {
  const open = useOpenQuickView(to);

  const openQuickView = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    open();
  };

  return <button {...rest} type={type} onClick={openQuickView} />;
}
